// Tournament data model (admin side).
//
// A tournament is the admin's pre-built version of a round: a course, a set
// of groups (each a foursome-or-smaller), and a tournament-wide set of games.
// Each group needs at least one CONNECTED player — someone with the app who
// scores for that group, exactly like a normal CaddieGames round where one
// player scores for the group. On the day, that player taps the tournament on
// their course-select screen and the first connected member to do so becomes
// the group scorer.
//
// This file is types + pure helpers only. Persistence lives in tournamentsApi.ts
// (admin) and tournamentPlay.ts (player side).

import { Course } from "./types";
import { ActiveGame } from "./activeRound";
import { OWNER_ID, RosterOwner, RosterPlayer, loadRoster } from "./roster";
import { tournamentGameName } from "./tournamentGames";

export type TournamentStatus = "draft" | "open" | "live" | "completed";

// Standard golf group size. Several games (Wolf, Hi-Lo, Hollywood) assume a
// foursome, so the wizard caps a group at four players.
export const MAX_GROUP_PLAYERS = 4;

// A player slot inside a group, copied from the admin's roster at build time
// (mirrors how a round snapshots roster data into round.players). A copy means
// later roster edits don't silently rewrite a published tournament.
export interface TournamentPlayer {
  // Roster player id this slot was filled from (OWNER_ID for the admin
  // themselves).
  rosterId: string;
  name: string;
  handicapIndex?: number;
  // Set when the roster player is a connected CaddieGames user. Presence here
  // means this player can be invited to score for the group.
  linkedUserId?: string;
  // The admin themselves — they have the app, so they can score for the group
  // without an invite. Treated as a scorer just like a connected player.
  isOwner?: boolean;
  // Tee this player plays from (tee id on the course). Defaults to the
  // tournament's default tee but can be overridden per player.
  tee?: string;
}

export interface TournamentGroup {
  id: string;
  // Label shown to players, e.g. "Group 1". Defaults to "Group N"; edited in
  // the Add-Players / Edit modal.
  name: string;
  // Optional tee time, free text (e.g. "2:10"). Edited alongside the name.
  teeTime?: string;
  players: TournamentPlayer[];
}

export interface Tournament {
  id: string;
  name: string;
  // Tee-off date as a plain calendar date (yyyy-mm-dd), no time zone.
  date: string;
  course: Course | null;
  // Default tee everyone is assigned when added; per-player tee can override.
  defaultTeeId?: string;
  groups: TournamentGroup[];
  // Tournament-wide — every group plays these games.
  games: ActiveGame[];
  status: TournamentStatus;
  createdAt: string;
  // Group ids whose card has been submitted (scorer ended the round). Returned
  // by the admin list endpoint; drives per-group "submitted ✓" indicators and
  // the derived "Ended" status. Not persisted on the tournament doc itself.
  submittedGroupIds?: string[];
}

// What the admin sees on the flag/pill. Derived (not stored): a published
// tournament is Pending before its date, Open on the day, and Ended once every
// group's card is final. A draft stays Draft until published.
export type TournamentDisplayStatus = "draft" | "pending" | "open" | "ended";

// True when every group has submitted its final card (and there's at least one
// group). This is the auto "Ended" condition.
export function allCardsIn(t: Tournament): boolean {
  const ids = t.groups.map((g) => g.id).filter(Boolean);
  if (ids.length === 0) return false;
  const submitted = new Set(t.submittedGroupIds ?? []);
  return ids.every((id) => submitted.has(id));
}

// Derive the display status from stored status + date + submitted cards.
// `todayISO` is yyyy-mm-dd (local); date comparison is lexicographic, which is
// correct for that format.
export function tournamentDisplayStatus(
  t: Tournament,
  todayISO: string,
): TournamentDisplayStatus {
  if (t.status === "draft") return "draft";
  if (allCardsIn(t)) return "ended";
  if (t.date && t.date > todayISO) return "pending";
  return "open";
}

// Lightweight shape the dashboard list renders. Derived from a Tournament so
// the row doesn't have to reach into nested groups/course on every render.
export interface TournamentSummary {
  id: string;
  name: string;
  courseName: string;
  date: string;
  status: TournamentStatus;
  groupCount: number;
  playerCount: number;
  games: string[];
}

function randomId(prefix: string): string {
  return `${prefix}${Math.random().toString(36).slice(2, 10)}`;
}

export function newGroup(index: number): TournamentGroup {
  return { id: randomId("g_"), name: `Group ${index + 1}`, players: [] };
}

export function emptyTournament(): Tournament {
  return {
    id: randomId("t_"),
    name: "",
    date: "",
    course: null,
    groups: [newGroup(0)],
    games: [],
    status: "draft",
    createdAt: new Date().toISOString(),
  };
}

// A roster player is connected (and therefore invitable as a group scorer)
// once it's been linked to a real CaddieGames user.
export function isConnected(p: { linkedUserId?: string }): boolean {
  return !!p.linkedUserId;
}

// Can this player do the scoring for their group? Connected players (get an
// invite) and the admin themselves (already in the app) both qualify.
export function canScore(p: TournamentPlayer): boolean {
  return isConnected(p) || !!p.isOwner;
}

export function rosterToTournamentPlayer(
  p: RosterPlayer,
  tee?: string,
): TournamentPlayer {
  return {
    rosterId: p.id,
    name: p.name,
    handicapIndex: p.handicapIndex,
    linkedUserId: p.linkedUserId,
    tee,
  };
}

// The admin themselves as a group player. OWNER_ID keeps them distinct from
// roster entries; isOwner marks them as a valid scorer.
export function ownerToTournamentPlayer(
  owner: RosterOwner,
  tee?: string,
): TournamentPlayer {
  return {
    rosterId: OWNER_ID,
    name: owner.name,
    handicapIndex: owner.handicapIndex,
    isOwner: true,
    tee,
  };
}

export function isOwnerAssigned(groups: TournamentGroup[]): boolean {
  return groups.some((g) => g.players.some((p) => p.rosterId === OWNER_ID));
}

// Re-pull each group player's handicap index from the CURRENT roster (and the
// owner card for the admin's own slot), returning a new Tournament. Handicaps
// are snapshotted at build time, so this is how an admin refreshes them after
// roster edits before the event. Players no longer on the roster keep their
// existing value. Does not touch any already-submitted cards (those are stored
// separately as the group's final scores).
export function resyncHandicaps(
  t: Tournament,
  roster: RosterPlayer[],
  owner: RosterOwner | null,
): Tournament {
  const byId = new Map(roster.map((r) => [r.id, r]));
  return {
    ...t,
    groups: t.groups.map((g) => ({
      ...g,
      players: g.players.map((p) => {
        if (p.isOwner) {
          return owner && typeof owner.handicapIndex === "number"
            ? { ...p, handicapIndex: owner.handicapIndex }
            : p;
        }
        const rp = byId.get(p.rosterId);
        return rp ? { ...p, handicapIndex: rp.handicapIndex } : p;
      }),
    })),
  };
}

// Roster players not yet assigned to any group — the pool the add-player
// picker draws from (a person plays in exactly one group).
export function availableRoster(groups: TournamentGroup[]): RosterPlayer[] {
  const taken = new Set<string>();
  for (const g of groups) for (const p of g.players) taken.add(p.rosterId);
  return loadRoster().filter((p) => !taken.has(p.id));
}

export function groupPlayerCount(t: Tournament): number {
  return t.groups.reduce((sum, g) => sum + g.players.length, 0);
}

// A group is ready when it has at least one player AND at least one of them can
// score for the group (connected player or the admin).
export function isGroupReady(g: TournamentGroup): boolean {
  return g.players.length > 0 && g.players.some(canScore);
}

// Stroke Play is always on for the leaderboard, so when no bet is set it's
// just the default scoring — not a "game" worth listing. Hidden from the
// displayed games list unless one of its bet fields is non-zero.
function isZeroBetStrokePlay(g: ActiveGame): boolean {
  if (g.id !== "strokePlay") return false;
  const o = g.options || {};
  return ["overallBet", "betPerPoint", "frontNineBet", "backNineBet"].every(
    (k) => !Number(o[k]),
  );
}

// Names of the games to show as "selected" for a tournament — enabled games,
// minus Stroke Play when it carries no bet.
export function displayedGameNames(games: ActiveGame[]): string[] {
  return games
    .filter((g) => g.enabled)
    .filter((g) => !isZeroBetStrokePlay(g))
    .map((g) => tournamentGameName(g.id));
}

export function summarize(t: Tournament): TournamentSummary {
  return {
    id: t.id,
    name: t.name,
    courseName: t.course?.name ?? "—",
    date: t.date,
    status: t.status,
    groupCount: t.groups.length,
    playerCount: groupPlayerCount(t),
    games: displayedGameNames(t.games),
  };
}
